
import { useState, useEffect, useRef } from 'react';
import { dataManager } from '../utils/dataManager';

/**
 * A generic hook for subscribing to data changes from the central dataManager.
 * Uses refs to avoid re-subscription cycles and stale closure comparisons.
 *
 * @param selector A function that returns the desired data slice from the dataManager.
 * @returns The selected data.
 */
export function useData<T>(selector: () => T): T {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const [data, setData] = useState(() => selector());
  const dataRef = useRef(data);

  useEffect(() => {
    const update = () => {
      const newData = selectorRef.current();
      if (JSON.stringify(dataRef.current) !== JSON.stringify(newData)) {
        dataRef.current = newData;
        setData(newData);
      }
    };

    const unsubscribe = dataManager.subscribe(update);
    update(); // Catch updates between mount and subscribe
    return () => unsubscribe();
  }, []); // Subscribe once, never re-subscribe

  return data;
}
